import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../components/Login/Alert';
import InputField from '../components/Login/InputField';
import Modal from '../components/Login/Modal';
import './LoginPage.css';

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', title: '', message: '' });
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // If already logged in, redirect to role-based default page
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (token && user._id && user.role) {
      const defaultPage = user.role === 'manager' ? '/accounts' : user.role === 'purchasing' ? '/products' : '/quotes';
      navigate(defaultPage, { replace: true });
      return;
    }

    const remembered = localStorage.getItem('rememberedUsername');
    if (remembered) {
      setFormData(prev => ({ ...prev, username: remembered }));
      setRememberMe(true);
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setAlert({ show: false });

    if (!formData.username || !formData.password) {
      setErrors({
        username: !formData.username ? '請輸入帳號' : '',
        password: !formData.password ? '請輸入密碼' : ''
      });
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: formData.username, password: formData.password })
      });

      const data = await response.json();

      if (response.ok) {
        setAlert({ show: true, type: 'success', title: '登入成功', message: '正在跳轉至系統首頁...' });
        
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data));
        
        if (rememberMe) {
          localStorage.setItem('rememberedUsername', formData.username);
        } else {
          localStorage.removeItem('rememberedUsername');
        }

        setTimeout(() => {
          navigate(data.role === 'manager' ? '/accounts' : data.role === 'purchasing' ? '/products' : '/quotes');
        }, 1500);
      } else {
        setAlert({ show: true, type: 'danger', title: '登入失敗', message: data.message || '帳號或密碼錯誤' });
        setLoading(false);
      }
    } catch (error) {
      setAlert({ show: true, type: 'danger', title: '登入失敗', message: '伺服器連線錯誤' });
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-wrapper">
          <div className="login-form-container">
            <div className="login-header">
              <div className="system-title">皇聚食品報價系統</div>
              <h1>歡迎回來</h1>
              <p>請輸入您的帳號密碼登入系統</p>
            </div>

            <Alert {...alert} />

            <form className="login-form" onSubmit={handleSubmit}>
              <InputField
                label="帳號"
                icon="👤"
                placeholder="輸入帳號"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                error={errors.username}
              />

              <InputField
                label="密碼"
                icon="🔒"
                placeholder="輸入密碼"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                error={errors.password}
                showPasswordToggle
              />

              <div className="form-options">
                <label className="remember-me">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>記住我的帳號</span>
                </label>
                <button type="button" className="forgot-password" onClick={(e) => { e.preventDefault(); setShowForgotModal(true); }}>
                  忘記密碼？
                </button>
              </div>

              <button type="submit" className={`btn btn-primary btn-block btn-lg ${loading ? 'btn-loading' : ''}`} disabled={loading}>
                登入系統
              </button>
            </form>

            <div className="divider"><span>系統支援角色</span></div>

            <div className="role-info">
              <div className="role-info-title">依據您的帳號角色，系統將提供對應的功能權限</div>
              <div className="role-badges">
                <span className="role-badge role-badge-manager">👔 主管</span>
                <span className="role-badge role-badge-purchaser">📦 採購</span>
                <span className="role-badge role-badge-sales">💼 業務</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="login-footer">
        <p>&copy; 2026 皇聚報價系統. All rights reserved.</p>
        <p>如有問題請聯繫 <a href="mailto:support@company.com">系統管理員</a></p>
      </footer>

      <Modal show={showForgotModal} onClose={() => setShowForgotModal(false)} title="🔑 忘記密碼">
        <p style={{ margin: '0 0 16px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          請聯繫系統主管重設您的密碼。基於安全考量，密碼重設需由主管在帳號管理頁面進行。
        </p>
        <div style={{ backgroundColor: 'var(--color-info-light)', borderRadius: 'var(--border-radius-md)', padding: 'var(--spacing-md)', display: 'flex', alignItems: 'flex-start', gap: 'var(--spacing-sm)' }}>
          <span style={{ color: 'var(--color-info-dark)', fontSize: '18px' }}>ℹ️</span>
          <div style={{ fontSize: '14px', color: 'var(--color-info-dark)' }}>
            <strong>聯繫方式：</strong><br />
            請洽詢您的部門主管或系統管理員
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LoginPage;
